import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  WashingMachine, Wind, Layers, Refrigerator, Flame, AirVent, ChefHat, Snowflake, Warehouse, Package,
  MessageCircle, X, ChevronLeft, ChevronRight, Images,
} from "lucide-react";
import { Reveal, SectionHead } from "./Shared";
import { waLink } from "@/lib/api";
import { CATEGORY_PHOTOS, SHOWROOM_PHOTOS } from "@/lib/photos";

const ICONS = { WashingMachine, Wind, Layers, Refrigerator, Flame, AirVent, ChefHat, Snowflake, Warehouse };

export default function Categories({ settings, categories }) {
  const [lightbox, setLightbox] = useState(null);

  if (!categories.length) return null;

  const photosFor = (cat) => {
    const photos = [...(CATEGORY_PHOTOS[cat.key] || [])];
    if (cat.image && !photos.some((p) => p.src === cat.image)) photos.unshift({ src: cat.image, label: cat.name });
    return photos;
  };

  const openLightbox = (cat) => {
    const photos = photosFor(cat);
    if (photos.length) setLightbox({ name: cat.name, photos, idx: 0 });
  };

  const move = (dir) =>
    setLightbox((lb) => ({ ...lb, idx: (lb.idx + dir + lb.photos.length) % lb.photos.length }));

  return (
    <section id="catalogo" data-testid="categories-section" className="bg-secondary/50 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHead
          num="02"
          testid="categories-title"
          title="Catálogo"
          sub="Explora nuestras categorías y cotiza por WhatsApp lo que te interese. Pronto verás aquí las fotos del showroom."
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="categories-grid">
          {categories.map((cat, i) => {
            const Icon = ICONS[cat.icon] || Package;
            const count = photosFor(cat).length;
            return (
              <Reveal key={cat.id || cat.key} delay={Math.min(i * 0.04, 0.25)}>
                <article
                  data-testid={`category-card-${cat.key || cat.name}`}
                  className="group flex h-full flex-col border border-border bg-white transition-shadow duration-300 hover:shadow-lg"
                >
                  {cat.image && (
                    <button
                      type="button"
                      onClick={() => openLightbox(cat)}
                      data-testid={`catalog-photo-${cat.key || i}`}
                      className="relative block w-full overflow-hidden text-left"
                      aria-label={`Ver fotos de ${cat.name}`}
                    >
                      <img
                        src={cat.image}
                        alt={`${cat.name} en San Pedro Sula — Electrodomésticos Rivera`}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <span className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-primary/90 px-3 py-1.5 text-xs font-semibold text-white">
                        <Images className="h-3.5 w-3.5" /> {count} {count === 1 ? "foto" : "fotos"}
                      </span>
                    </button>
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-extrabold tracking-tight text-primary">
                        <Link to={`/${cat.key}`} data-testid={`category-link-${cat.key || i}`} className="transition-colors duration-300 hover:text-primary/60">
                          {cat.name}
                        </Link>
                      </h3>
                      <Icon className="h-6 w-6 text-primary/50" />
                    </div>
                    {cat.blurb && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{cat.blurb}</p>}
                    <a
                      data-testid={`category-whatsapp-${cat.key || i}`}
                      href={waLink(settings, `Hola Electrodomésticos Rivera, quiero cotizar: ${cat.name}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-2 pt-1 text-sm font-semibold text-whatsapp transition-transform duration-300 hover:-translate-y-0.5"
                    >
                      <MessageCircle className="h-4 w-4" /> Cotizar por WhatsApp
                    </a>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        {SHOWROOM_PHOTOS.length > 0 && (
        <div className="mt-16 border-t border-border pt-10">
          <Reveal>
            <h3 className="text-xl font-extrabold tracking-tight text-primary" data-testid="showroom-strip-title">
              El showroom, por dentro y por fuera
            </h3>
          </Reveal>
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="showroom-strip">
            {SHOWROOM_PHOTOS.map((p, i) => (
              <Reveal key={p.src} delay={Math.min(i * 0.05, 0.2)}>
                <figure className="group relative overflow-hidden border border-border bg-white">
                  <img
                    src={p.src}
                    alt={`${p.label} — Electrodomésticos Rivera, San Pedro Sula`}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    data-testid={`showroom-photo-${i}`}
                  />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-primary/85 px-3 py-2 text-xs font-semibold text-white">
                    {p.label}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col bg-primary/95 backdrop-blur-md"
            data-testid="catalog-lightbox"
          >
            <div className="flex items-center justify-between px-5 py-4 text-white md:px-8">
              <p className="text-sm font-extrabold tracking-tight md:text-base" data-testid="lightbox-title">
                {lightbox.name} <span className="ml-2 font-medium text-white/60">{lightbox.idx + 1} / {lightbox.photos.length}</span>
              </p>
              <button
                type="button"
                onClick={() => setLightbox(null)}
                data-testid="lightbox-close"
                aria-label="Cerrar"
                className="flex h-11 w-11 items-center justify-center border border-white/30 transition-colors duration-300 hover:bg-white hover:text-primary"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="relative flex flex-1 items-center justify-center overflow-hidden px-14 pb-6 md:px-24">
              <motion.img
                key={lightbox.photos[lightbox.idx].src}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                src={lightbox.photos[lightbox.idx].src}
                alt={lightbox.photos[lightbox.idx].label}
                className="max-h-full max-w-full object-contain"
                data-testid="lightbox-image"
              />
              {lightbox.photos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => move(-1)}
                    data-testid="lightbox-prev"
                    aria-label="Anterior"
                    className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/30 text-white transition-colors duration-300 hover:bg-white hover:text-primary md:left-6"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(1)}
                    data-testid="lightbox-next"
                    aria-label="Siguiente"
                    className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center border border-white/30 text-white transition-colors duration-300 hover:bg-white hover:text-primary md:right-6"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
            <p className="pb-5 text-center text-xs font-semibold text-white/70" data-testid="lightbox-caption">
              {lightbox.photos[lightbox.idx].label}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
